import { useState } from 'react'
import { AlertTriangle, Pencil, Plus, Ruler, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/features/auth/useAuth'
import { useMaterials, useUnits } from '@/features/materials/useMaterials'
import { UnitEditDialog } from '@/features/units/UnitEditDialog'
import { deleteUnit } from '@/features/units/units-api'
import { describeWriteError } from '@/features/users/permissions'
import { DeepLinkMissing } from '@/components/DeepLinkMissing'
import { useDeepLink } from '@/lib/use-deep-link'
import type { UnitOfMeasure } from '@/lib/types'

/** Units of measurement: the shared catalog behind the "unit" field of every material. */
export function UnitsPage() {
  const { user, profile } = useAuth()
  const { units, isLoading, error } = useUnits()
  // Only to warn how many materials use a unit before it is deleted.
  const { materials } = useMaterials(user?.uid)

  const [editing, setEditing] = useState<UnitOfMeasure | null>(null)
  const [isCreating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<UnitOfMeasure | null>(null)
  const [writeError, setWriteError] = useState<string | null>(null)
  // `/units?edit=<id>` (links from "Несоответствия") opens that unit's editor.
  const deepLink = useDeepLink('edit', units, !isLoading, setEditing)

  const actor = {
    uid: user?.uid ?? '',
    label: profile?.email ?? user?.email ?? user?.uid ?? '',
  }

  // A material references a unit by id, but legacy items only carry the symbol as text.
  const usageOf = (unit: UnitOfMeasure) =>
    materials.filter((item) => item.unitId === unit.id || (item.unitId === '' && item.unit === unit.symbol))
      .length

  const handleDelete = async () => {
    if (!deleting) return
    setWriteError(null)
    try {
      await deleteUnit(deleting.id)
      setDeleting(null)
    } catch (caught) {
      setWriteError(describeWriteError(caught))
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Единицы измерения</h1>
          <p className="text-muted-foreground text-sm">
            Общий справочник: символ отсюда подставляется в пункты затрат и печатается в сметах. Менять
            может только администратор.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus />
          Добавить
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Справочник не загружен</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <DeepLinkMissing missingId={deepLink.missingId} what="Единица" onDismiss={deepLink.dismiss} />

      {writeError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Действие не выполнено</AlertTitle>
          <AlertDescription>{writeError}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="py-0">
        <CardContent className="px-0">
          {isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : units.length === 0 ? (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-16 text-sm">
              <Ruler className="size-8" />
              Единиц пока нет.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Название</TableHead>
                  <TableHead>Символ</TableHead>
                  <TableHead>Состояние</TableHead>
                  <TableHead>Пунктов</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {units.map((unit) => (
                  <TableRow key={unit.id} className={unit.isActive ? undefined : 'opacity-60'}>
                    <TableCell className="font-medium">{unit.name}</TableCell>
                    <TableCell className="text-sm">{unit.symbol || '—'}</TableCell>
                    <TableCell>
                      {unit.isActive ? (
                        <Badge variant="outline">активна</Badge>
                      ) : (
                        <Badge variant="secondary">скрыта</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{usageOf(unit)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(unit)}>
                        <Pencil />
                        Изменить
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleting(unit)}
                        aria-label={`Удалить единицу ${unit.name}`}
                      >
                        <Trash2 />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-muted-foreground text-xs">
        Удалять единицу, которая уже используется, не стоит: пункты затрат хранят символ строкой,
        поэтому сметы продолжат печататься, но ссылка на справочник у них станет битой. Обычно
        правильнее поставить «скрыта» — тогда единица останется у старых пунктов, но не будет
        предлагаться при выборе.
      </p>

      {isCreating ? (
        <UnitEditDialog unit={null} actor={actor} onClose={() => setCreating(false)} />
      ) : null}

      {editing ? (
        <UnitEditDialog unit={editing} actor={actor} onClose={() => setEditing(null)} />
      ) : null}

      {deleting ? (
        <Dialog open onOpenChange={(open) => !open && setDeleting(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Удалить единицу?</DialogTitle>
              <DialogDescription>
                «{deleting.name}» ({deleting.symbol}) будет удалена. Пунктов затрат с этой единицей:{' '}
                {usageOf(deleting)}. Символ у них останется, но ссылка на справочник станет битой —
                возможно, лучше поставить «скрыта».
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)}>
                Отмена
              </Button>
              <Button variant="destructive" onClick={() => void handleDelete()}>
                <Trash2 />
                Удалить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}
